import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Button, Box, CircularProgress, Container, Paper, Divider } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../../utils/api';

interface Document {
  _id: string;
  title: string;
  content: string;
  required: boolean;
  order: number;
}

interface ApplicationInfo {
  counsellor: {
    firstName: string;
    lastName: string;
    email: string;
  };
  applicationInfoViewed: boolean;
  applicationDeclined: boolean;
}

function ApplicationInfoPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [applicationInfo, setApplicationInfo] = useState<ApplicationInfo | null>(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch application info
        const infoResponse = await api.get(`/api/counsellors/public/application-info/${token}`);
        setApplicationInfo(infoResponse.data);

        // If they already viewed the info and said yes, redirect to application form
        if (infoResponse.data.applicationInfoViewed && !infoResponse.data.applicationDeclined) {
          navigate(`/application-form/${token}`);
          return;
        }

        // If they declined, show expired message
        if (infoResponse.data.applicationDeclined) {
          navigate('/application-link-expired');
          return;
        }

        // Fetch pre-application documents
        const docsResponse = await api.get('/api/counsellors/public/pre-application-documents');
        setDocuments(docsResponse.data);
        
        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        if (err.response?.status === 410 || err.response?.status === 404) {
          navigate('/application-link-expired');
        } else {
          setError('Failed to load application information. Please try again.');
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [token, navigate]);

  const handleDecision = async (proceed: boolean) => {
    try {
      setSubmitting(true);
      const response = await api.post(`/api/counsellors/public/application-info/${token}/decision`, {
        proceed
      });

      if (proceed) {
        // Redirect to application form
        navigate(response.data.redirectTo);
      } else {
        // Show thank you message and redirect
        navigate('/application-declined');
      }
    } catch (err: any) {
      console.error('Error recording decision:', err);
      if (err.response?.status === 410) {
        navigate('/application-link-expired');
      } else {
        setError('Failed to record your decision. Please try again.');
      }
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h5" color="error" gutterBottom>
                Error
              </Typography>
              <Typography variant="body1" sx={{ mt: 2, mb: 3 }}>
                {error}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Welcome to Evolving Minds
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Hello {applicationInfo?.counsellor.firstName} {applicationInfo?.counsellor.lastName}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Before you begin your application, please take a moment to review the following information about our organization.
            </Typography>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Documents */}
          <Box sx={{ mb: 4 }}>
            {documents.map((doc, index) => (
              <Box key={doc._id} sx={{ mb: 4 }}>
                <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                    {doc.title}
                  </Typography>
                  <Box
                    sx={{ 
                      color: 'text.primary',
                      lineHeight: 1.7,
                      '& p': { mb: 2 },
                      '& ul, & ol': { pl: 3, mb: 2 },
                      '& li': { mb: 1 },
                      '& h1': { fontSize: '2rem', fontWeight: 700, mb: 2, mt: 2 },
                      '& h2': { fontSize: '1.5rem', fontWeight: 600, mb: 2, mt: 2 },
                      '& h3': { fontSize: '1.25rem', fontWeight: 600, mb: 1, mt: 1 },
                      '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
                      '& th': { border: '1px solid #ddd', padding: '8px', backgroundColor: '#f5f5f5', fontWeight: 600, textAlign: 'left' },
                      '& td': { border: '1px solid #ddd', padding: '8px' },
                      '& strong': { fontWeight: 600 }
                    }}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.content}</ReactMarkdown>
                  </Box>
                </Paper>
              </Box>
            ))}
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Decision Section */}
          <Paper elevation={2} sx={{ p: 4, backgroundColor: '#e3f2fd', textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              Would you like to proceed with the application?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              After reviewing the information above, please let us know if you would like to continue with your counsellor application.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleDecision(false)}
                disabled={submitting}
                sx={{
                  minWidth: '180px',
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                No, Thank You
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleDecision(true)}
                disabled={submitting}
                sx={{
                  minWidth: '180px',
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {submitting ? 'Processing...' : 'Yes, Proceed to Application'}
              </Button>
            </Box>
          </Paper>
        </Paper>
      </Box>
    </Container>
  );
}

export default ApplicationInfoPage;
